import React, { useMemo } from 'react';
import { GridRow, GridColumn, SelectionContext } from '../types/gridTypes';
import '../styles/theme.css';

interface GridStatusBarProps {
  rows: GridRow[];
  columns: GridColumn[];
  selection: SelectionContext;
}

interface SelectionStats {
  sum: number;
  average: number;
  min: number;
  max: number;
  count: number;
  numericCount: number;
}

export const GridStatusBar: React.FC<GridStatusBarProps> = ({
  rows,
  columns,
  selection,
}) => {
  const stats = useMemo<SelectionStats | null>(() => {
    // Only calculate for range, row, or column selection (not single cell)
    if (selection.mode === 'none' || selection.mode === 'cell') {
       // Also check if we have multiple cells selected even in 'cell' mode (legacy overlap)
       // But typically strict cell mode implies one cell. Range implies multiple.
       // Let's rely on array lengths too.
       if (selection.selectedRowIds.length <= 1 && selection.selectedColumnIds.length <= 1) {
         return null;
       }
    }

    const { selectedRowIds, selectedColumnIds } = selection;
    
    // Quick lookups
    const rowMap = new Map(rows.map(r => [r.id, r]));
    // Filter columns to only those selected and are number/currency/percent
    const relevantCols = columns.filter(c => 
      selectedColumnIds.includes(c.id) && 
      ['number', 'currency', 'percent'].includes(c.type)
    );

    if (relevantCols.length === 0) {
        // We can still show Count but Sum/Avg requires numeric columns
        const totalCells = selectedRowIds.length * selectedColumnIds.length;
        if (totalCells > 1) {
            return { sum: 0, average: 0, min: 0, max: 0, count: totalCells, numericCount: 0 };
        }
        return null;
    }

    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let numericCount = 0;
    let totalCount = 0;

    selectedRowIds.forEach(rowId => {
      const row = rowMap.get(rowId);
      if (!row) return;

      selectedColumnIds.forEach(colId => {
        totalCount++;
        // Check if this column is one we want to aggregate selection for
        const col = relevantCols.find(c => c.id === colId);
        if (!col) return;

        let val = row.values[colId];
        
        // Basic parsing if string
        if (typeof val === 'string') {
            // Remove currency symbols, commas, etc
            const clean = val.replace(/[$,]/g, '');
            if (!isNaN(parseFloat(clean)) && clean.trim() !== '') {
                val = parseFloat(clean);
            }
        }

        if (typeof val === 'number' && !isNaN(val)) {
          sum += val;
          min = Math.min(min, val);
          max = Math.max(max, val);
          numericCount++;
        }
      });
    });

    if (numericCount === 0) {
        return { sum: 0, average: 0, min: 0, max: 0, count: totalCount, numericCount: 0 };
    }

    return {
      sum,
      average: sum / numericCount,
      min,
      max,
      count: totalCount,
      numericCount
    };
  }, [rows, columns, selection]);

  if (!stats || stats.count <= 1) {
    return null;
  }

  return (
    <div style={{
      height: '32px',
      backgroundColor: 'var(--grid-surface)',
      borderTop: '1px solid var(--grid-border-medium)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '0 24px',
      gap: '24px',
      fontSize: '13px',
      color: 'var(--grid-text-secondary)',
      flexShrink: 0,
      userSelect: 'none'
    }}>
      {stats.numericCount > 0 && (
        <>
          <div title="Sum of selected cells">
            Sum: <span style={{ color: 'var(--grid-text-primary)', fontWeight: 500 }}>
              {stats.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
          <div title="Average of selected cells">
            Avg: <span style={{ color: 'var(--grid-text-primary)', fontWeight: 500 }}>
              {stats.average.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
          <div title="Minimum value">
            Min: <span style={{ color: 'var(--grid-text-primary)', fontWeight: 500 }}>
              {stats.min.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
          <div title="Maximum value">
            Max: <span style={{ color: 'var(--grid-text-primary)', fontWeight: 500 }}>
              {stats.max.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
        </>
      )}
      <div title="Count of selected cells">
        Count: <span style={{ color: 'var(--grid-text-primary)', fontWeight: 500 }}>
          {stats.count}
        </span>
      </div>
    </div>
  );
};
